import Cookies from 'js-cookie';
import Vue from 'vue';
import { PersistedSession } from '../../types';
import Session from '../models/Session';
import User from '../models/User';
import Conn from './Conn';
import ObservedPromise from './ObservedPromise';

type LoginCredentials = { email: string; password: string };

let _default!: AuthController;

export default class AuthController {
  static get default(): AuthController {
    if (!_default) {
      _default = new AuthController();
    }
    return _default;
  }

  static connInstance: Conn | undefined;

  private store = Vue.observable({
    init: new ObservedPromise(false),
    session: new ObservedPromise<PersistedSession>(),
  });

  offlineSupport = false;

  get isInitialized() {
    return Boolean(this.store.init.value);
  }

  get isAuthenticating() {
    return this.store.session.isPending;
  }

  get isError() {
    return this.store.session.isError;
  }

  get error() {
    return this.store.session.error;
  }

  get session() {
    return this.store.session.value;
  }

  get user() {
    return this.session?.user;
  }

  get isValidSession(): boolean {
    const { session } = this;
    if (!session) {
      return false;
    }
    const { id, user } = session;
    return Boolean(id && user && user.id);
  }

  get isValidUser(): boolean {
    return Boolean(this.user?.id);
  }

  // Alias of `isValidSession`
  get isLoggedIn(): boolean {
    return this.isValidSession;
  }

  clearSession() {
    this.store.session = new ObservedPromise();
  }

  // Log-in with explicit email/password
  async login({ email, password }: LoginCredentials) {
    this.newSession({ email, password });
    await this.waitForAuthentication({ silent: false });
    return this.isLoggedIn;
  }

  // Creates a new session with explicit email/password
  newSession({ email, password }: LoginCredentials) {
    const promise = (async () => {
      if (process.env.NODE_ENV === 'development') {
        if (
          User.demoUser?.id === 'demo' &&
          email === User.demoUser.email &&
          password === 'demodemo'
        ) {
          return new Session({
            id: 'demo',
            user: User.demoUser,
          }) as PersistedSession;
        }
      }
      const session = new Session({ email, password });
      await session.save();
      if (session.user?.id) {
        session.user = (await User.find(session.user.id)).data;
      }
      if (this.offlineSupport) {
        await offlineStoreSession(session);
      }
      return session as PersistedSession;
    })();
    this.store.session = new ObservedPromise(promise);
  }

  // Alias of `loginAgain`
  async getCurrentUser() {
    return this.loginAgain();
  }

  // Log-in with previous session, if available
  async loginAgain() {
    this.loginWithPreviousSession();
    await this.waitForAuthentication({ silent: false });
    return this.isLoggedIn;
  }

  // Creates a new session with previous credentials, if available
  loginWithPreviousSession() {
    let promise = (async () => {
      const result = await Session.first();
      if (!result.data) {
        return undefined;
      }
      const { data: session } = result;
      if (session.user?.id) {
        session.user = (await User.find(session.user.id)).data;
      }
      if (this.offlineSupport) {
        await offlineStoreSession(session);
      }
      return session as PersistedSession;
    })();
    if (this.offlineSupport) {
      promise = promise.catch(async err => {
        if (await this.isOffline()) {
          const session = await offlineRestoreSession();
          if (session) {
            Object.defineProperty(session, '__offline', { value: true });
            return session;
          }
        }
        throw err;
      });
    }
    this.store.session = new ObservedPromise(promise);
  }

  async logout() {
    if (this.isLoggedIn) {
      await new Session().destroy();
    }
    if (this.offlineSupport) {
      await offlineClear();
    }
    this.clearSession();
  }

  async waitForAuthentication({ silent = true } = {}) {
    if (!this.isAuthenticating) {
      return;
    }
    try {
      await this.store.session.promise;
    } catch (err) {
      if (silent) {
        // Ignore error, which is available via this.error
        return;
      }
      throw err;
    }
  }

  initialize() {
    if (this.store.init.value !== false) {
      // Only run once
      return;
    }
    const promise = (async () => {
      if (!this.isValidSession) {
        this.loginWithPreviousSession();
      }
      await this.waitForAuthentication();
      return true;
    })();
    this.store.init = new ObservedPromise(promise);
  }

  async ensureInitialized() {
    if (this.isInitialized) {
      return;
    }
    if (this.store.init.isPending) {
      await this.store.init.promise;
      return;
    }
    this.initialize();
    await this.store.init.promise;
  }

  async ensureIdle() {
    await this.ensureInitialized();
    if (
      this.offlineSupport &&
      (await this.isOnline()) &&
      (this.session as HashAny)?.__offline
    ) {
      this.loginWithPreviousSession();
    }
    if (this.isAuthenticating) {
      await this.waitForAuthentication();
    }
  }

  async isOnline() {
    if (AuthController.connInstance) {
      return AuthController.connInstance.checkIsOnline();
    }
    return Conn.default.checkIsOnline();
  }

  async isOffline() {
    if (AuthController.connInstance) {
      return AuthController.connInstance.checkIsOffline();
    }
    return Conn.default.checkIsOffline();
  }
}

async function offlineStoreSession(session: Session) {
  const token = Cookies.get('csrftoken');
  if (!token) {
    return;
  }
  let data = JSON.stringify({
    token,
    session: session.asJSON(['user']),
  });
  data = window.btoa(data);
  localStorage.setItem('offlineSession', data);
}

async function offlineRestoreSession(): Promise<PersistedSession | undefined> {
  let data = localStorage.getItem('offlineSession');
  if (!data) {
    return undefined;
  }
  data = window.atob(data);
  const json = JSON.parse(data);
  if (!json) {
    return undefined;
  }
  const { token, session } = json;
  if (!token || !session || token !== Cookies.get('csrftoken')) {
    return undefined;
  }
  return Session.fromJsonapi(session.data, session);
}

async function offlineClear() {
  localStorage.removeItem('offlineSession');
}
